import { useAuth } from "src/hooks/useAuth";
import { useEffect } from "react";
import { signOut } from "src/utils/nextAuth";
import { SessionError } from "src/pages/api/auth/getUserTokenFromApi";
import { isAppWebview } from "src/utils/isAppWebview";
import {
  getSessionStorageItem,
  setSessionStorageItem,
} from "src/utils/helper/sessionStorage";

const SIGNOUT_ATTEMPTED_FLAG = "ot_session_error_signout";

// next-auth's server-side Set-Cookie can fail to clear the cookie if the
// host that originally set it differs from the host signOut is invoked on
// (Cloudflare force-adding `www.` vs. a bare NEXTAUTH_URL). Wipe the
// auth-related cookies from the client across all plausible Domain scopes
// so the user can recover without manual DevTools cleanup.
const AUTH_COOKIES = [
  "next-auth.session-token",
  "next-auth.csrf-token",
  "ot_auth_token",
];

function getDomainVariants(): string[] {
  if (typeof window === "undefined") return [];
  const host = window.location.hostname;
  const parts = host.split(".");
  const variants = new Set<string>();
  variants.add(host);
  variants.add(`.${host}`);
  if (parts.length > 2) {
    const apex = parts.slice(-2).join(".");
    variants.add(apex);
    variants.add(`.${apex}`);
  }
  return Array.from(variants);
}

function forceClearAuthCookies(): void {
  if (typeof document === "undefined") return;
  const domains = getDomainVariants();
  for (const cookieName of AUTH_COOKIES) {
    // No-domain variant (cookies set without a Domain attribute).
    document.cookie = `${cookieName}=; Max-Age=0; Path=/`;
    for (const domain of domains) {
      document.cookie = `${cookieName}=; Max-Age=0; Path=/; Domain=${domain}`;
    }
  }
}

export const useSessionError = (): void => {
  const { session } = useAuth();

  useEffect(() => {
    if (session?.error !== SessionError.REFRESH_ACCESS_TOKEN_ERROR) {
      return;
    }

    if (isAppWebview()) {
      return;
    }

    // If signOut can't actually clear the cookie (e.g. cookie-domain mismatch
    // from a Cloudflare-rewritten host), retrying would loop forever. Try
    // once per tab; after that, let the app proceed with the errored session.
    if (
      typeof window === "undefined" ||
      getSessionStorageItem(SIGNOUT_ATTEMPTED_FLAG)
    ) {
      return;
    }
    setSessionStorageItem(SIGNOUT_ATTEMPTED_FLAG, "1");
    forceClearAuthCookies();
    void signOut();
  }, [session]);
};
